// Полоса вкладок карточки. Одна на все карточки продукта.
//
// Заведена 25.08.2026. До неё высот было ДВЕ: карточка подотчётного лица брала полосу как
// есть — 48 точек, зазор 24 и рамки сверху и снизу, — а карточки человека и объекта
// переопределяли её классами до 40 точек, зазора 20 и без рамок. При переходе «Деньги → Люди»
// полоса прыгала на восемь точек и меняла наличие линии, то есть шапка экрана дёргалась на
// каждом переключении раздела.
//
// Выбрана меньшая: рамки у полосы не нужно вовсе — нижнюю линию уже несёт шапка колонки
// детали, и вместе получалась двойная. А восемь сэкономленных точек — это ещё одна видимая
// строка списка под ней.
//
// Классы живут ЗДЕСЬ, а не в трёх карточках: переопределение, повторённое трижды, — это и
// есть способ, которым высота разъезжается.
//
// ## Стрелки прокрутки, 04.09.2026
//
// Замер на ноутбучной ширине: семь вкладок карточки человека требуют 674 точки, колонка
// детали даёт 584. Полоса и раньше прокручивалась, но молча — седьмая вкладка обрывалась
// посреди слова («Исто»), а докрутить её мышью было нечем: колесо в горизонтальной полосе на
// Windows не работает без Shift, полосы прокрутки под ней нет.
//
// Стрелки появляются ТОЛЬКО когда есть куда крутить, и каждая гаснет, дойдя до своего края.
// Постоянные стрелки на широком мониторе, где всё влезает, — это две лишние цели касания и
// обещание, что за краем что-то есть.
//
// Почему не перенос на вторую строку: подчёркивание активной вкладки в примитиве считается
// от левого края и рисуется по нижнему краю полосы — на второй строке оно оказалось бы под
// чужой вкладкой. Почему не сокращение подписей: «История статусов» короче «Истории» не
// делается без потери смысла, а выигрыш всё равно меньше девяноста точек.

import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';

import * as TabMenu from '@/shared/ui/tab-menu-horizontal';
import { cn } from '@/utils/cn';

/** На сколько крутит одно нажатие: примерно две вкладки. */
const ШАГ = 160;

export function CardTabsList({
  children,
  className = 'h-10 gap-5 border-y-0',
  подпись,
}: {
  children: ReactNode;
  /**
   * Классы самого списка вкладок. По умолчанию — полоса карточки: без рамок, нижнюю линию несёт
   * шапка колонки детали. Разделу «Суточные расходы» нужна своя линия снизу (`border-t-0`): его
   * полоса стоит в шапке колонки списка, где ниже неё идёт другая строка шапки, и без линии
   * она висела бы в воздухе. Ширина и стрелки прокрутки у всех одни и те же.
   */
  className?: string;
  /**
   * Название полосы для читалки с экрана и для поиска в проверках: «Состояние людей». Нужно там,
   * где полос на экране несколько: над списком людей стоят полоса состояний и, когда тип
   * оформления заполнен, полоса представлений, и без названия их не различить.
   */
  подпись?: string;
}) {
  const обёртка = useRef<HTMLDivElement>(null);
  const [влево, показатьВлево] = useState(false);
  const [вправо, показатьВправо] = useState(false);

  const пересчитать = useCallback(() => {
    const узел = обёртка.current;
    if (узел === null) {
      return;
    }
    // Единица допуска: браузер отдаёт дробные значения, и на целиком прокрученной полосе
    // остаток бывает 0,5 — стрелка иначе горела бы всегда.
    показатьВлево(узел.scrollLeft > 1);
    показатьВправо(узел.scrollLeft + узел.clientWidth < узел.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const узел = обёртка.current;
    if (узел === null) {
      return;
    }
    пересчитать();
    узел.addEventListener('scroll', пересчитать, { passive: true });
    // Ширина колонки детали меняется вместе с окном, а не только при первой отрисовке.
    const наблюдатель = new ResizeObserver(пересчитать);
    наблюдатель.observe(узел);
    return () => {
      узел.removeEventListener('scroll', пересчитать);
      наблюдатель.disconnect();
    };
  }, [пересчитать, children]);

  function крутить(куда: -1 | 1) {
    обёртка.current?.scrollBy({ left: куда * ШАГ, behavior: 'smooth' });
  }

  return (
    <div className='relative'>
      <TabMenu.List wrapperRef={обёртка} className={className} aria-label={подпись}>
        {children}
      </TabMenu.List>
      {влево && <Стрелка сторона='влево' onClick={() => крутить(-1)} />}
      {вправо && <Стрелка сторона='вправо' onClick={() => крутить(1)} />}
    </div>
  );
}

function Стрелка({ сторона, onClick }: { сторона: 'влево' | 'вправо'; onClick: () => void }) {
  const Значок = сторона === 'влево' ? RiArrowLeftSLine : RiArrowRightSLine;
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={сторона === 'влево' ? 'Показать вкладки левее' : 'Показать вкладки правее'}
      className={cn(
        // Подложка сплошная, а не полупрозрачная: под стрелкой проезжают названия вкладок, и
        // сквозь неё они читались бы как опечатка.
        'цель-тача absolute top-0 flex h-10 w-7 items-center justify-center bg-bg-white-0 text-text-sub-600',
        'transition duration-200 ease-out hover:text-text-strong-950',
        сторона === 'влево' ? 'left-0' : 'right-0',
      )}
    >
      <Значок className='size-4' />
    </button>
  );
}

export function CardTab({ value, children }: { value: string; children: ReactNode }) {
  return (
    <TabMenu.Trigger value={value} className='цель-тача relative h-10 py-2.5'>
      {children}
    </TabMenu.Trigger>
  );
}
